import { useEffect, useState } from 'react';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';

const RESEND_SECONDS = 60;

// Shared by sign-up and "Forgot password": send a code, count down before
// it can be resent, verify it (→ ticket). Error messages are translated;
// a wrong code shows how many attempts are left.
export function useCodeFlow(purpose) {
  const { t, lang, errorText } = useI18n();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email'); // email → code → final
  const [ticket, setTicket] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const messageFor = (err) => (err.code === 'code_invalid' && err.data?.attemptsLeft
    ? t('errors.code_invalid_left', { n: err.data.attemptsLeft })
    : errorText(err));

  async function send(extra = {}) {
    setError(''); setNotice(''); setBusy(true);
    try {
      await api('/auth/code/send', { method: 'POST', body: { email: email.trim(), purpose, lang, ...extra } });
      setStep('code'); setCode(''); setCooldown(RESEND_SECONDS);
      return true;
    } catch (err) {
      setError(messageFor(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function verify(value = code) {
    if (value.length !== 6 || busy) return;
    setError(''); setNotice(''); setBusy(true);
    try {
      const r = await api('/auth/code/verify', { method: 'POST', body: { email: email.trim(), purpose, code: value } });
      setTicket(r.ticket); setStep('final');
    } catch (err) {
      setError(messageFor(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  const changeEmail = () => { setStep('email'); setCode(''); setError(''); setNotice(''); };

  return { email, setEmail, code, setCode, step, setStep, ticket, cooldown, busy, setBusy, error, setError, notice, setNotice, send, verify, changeEmail, messageFor };
}
